import { t } from "i18next";
import { useContext, useEffect, useMemo, useState } from "react";
import DBModal from "../../component/global/DBModal";
import DBButton from "../../component/global/DBButton";
import DBInput from "../../component/global/DBInput";
import DBSpinner from "../../component/global/DBSpinner";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import { SURVEY_LIST, SURVEY_TEMPLATES } from "../../helperData/ApiAddress";
import {
    SURVEY_SUBJECT,
    groupSurveyTemplates,
    surveyTemplateItemCount,
} from "./constants";

const categoryLabel = (category) => {
    const key = `survey.template.category.${category || "other"}`;
    const label = t(key);
    return label === key ? t("survey.template.category.other") : label;
};

export default function TemplateSelectModal({
    show,
    onHide,
    onSuccess,
    personal = false,
}) {
    const toast = useContext(ToastContext);
    const [templates, setTemplates] = useState([]);
    const [selected, setSelected] = useState(null);
    const [search, setSearch] = useState("");

    const [getTemplates, getTemplatesLoading] = useRequest(
        SURVEY_TEMPLATES,
        "GET"
    );
    const [createSurvey, createLoading] = useRequest(SURVEY_LIST, "POST");

    useEffect(() => {
        if (!show) return;
        setSelected(null);
        setSearch("");
        getTemplates({ page_size: 100 })
            .then((r) => setTemplates(r.results || r || []))
            .catch(() => setTemplates([]));
    }, [show]);

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return templates;
        return templates.filter((template) => {
            const haystack = [
                template.name,
                template.description,
                categoryLabel(template.category || "other"),
            ]
                .join(" ")
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [templates, search]);

    const groups = useMemo(
        () => groupSurveyTemplates(filtered),
        [filtered]
    );

    const handleCreate = () => {
        if (!selected) return;
        createSurvey({
            title: selected.name,
            description: selected.description || "",
            subject_type: SURVEY_SUBJECT.FREE,
            subject_title: selected.name,
            access_mode: 1,
            anonymous_responses: false,
            require_captcha: false,
            consent_text: "",
            retention_days: null,
            ai_enabled: false,
            version: 1,
            form_structure: selected.structure,
            ...(personal ? { personal: true } : {}),
        })
            .then((saved) => {
                toast(t("survey.save.success"), "success");
                onSuccess?.(saved, { isCreate: true });
            })
            .catch(() => toast(t("survey.save.error"), "danger"));
    };

    return (
        <DBModal
            permanent
            show={show}
            minWidth={640}
            onHide={onHide}
            className="survey-template-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                closeLabel={t("survey.settings.help.close")}
                title={t("survey.template.select")}
            />
            <DBModal.Content>
                {getTemplatesLoading ? (
                    <DBSpinner tip={t("loading")} />
                ) : templates.length === 0 ? (
                    <div className="surveys-empty">
                        {t("survey.template.empty")}
                    </div>
                ) : (
                    <>
                        <div className="survey-template-search">
                            <DBInput
                                size="large"
                                value={search}
                                placeholder={t("survey.template.search")}
                                onChange={(event) =>
                                    setSearch(event.target.value)
                                }
                            />
                        </div>
                        {groups.length === 0 ? (
                            <div className="surveys-empty">
                                {t("survey.template.empty")}
                            </div>
                        ) : (
                            groups.map((group) => (
                                <div
                                    className="survey-template-group"
                                    key={group.category}
                                >
                                    <h3>{categoryLabel(group.category)}</h3>
                                    <div className="survey-template-list">
                                        {group.templates.map((template) => (
                                            <button
                                                type="button"
                                                key={template.id}
                                                className={
                                                    selected?.id ===
                                                    template.id
                                                        ? "selected"
                                                        : ""
                                                }
                                                onClick={() =>
                                                    setSelected(template)
                                                }
                                            >
                                                <strong>
                                                    {template.name}
                                                </strong>
                                                {template.description && (
                                                    <span>
                                                        {template.description}
                                                    </span>
                                                )}
                                                <small>
                                                    {t(
                                                        "survey.template.item_count",
                                                        {
                                                            count: surveyTemplateItemCount(
                                                                template
                                                            ),
                                                        }
                                                    )}
                                                </small>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    caption={t("survey.create")}
                    onClick={handleCreate}
                    loading={createLoading}
                    disabled={!selected}
                />
                <DBButton
                    caption={t("cancel")}
                    type="secondary"
                    onClick={onHide}
                />
            </DBModal.Footer>
        </DBModal>
    );
}
